import React, { useState, useEffect } from 'react';
import '../styles/AddStudentModal.css';
import API_URL from "../apiConfig";

// Helper to get session year for a class/year
function getSessionYear(batch, classNum) {
  if (!batch || !batch.name) return "";
  const parts = String(batch.name).split("-");
  const startYear = parseInt(parts[0], 10);
  if (isNaN(startYear)) return batch.name;
  const s = startYear + (classNum - 1);
  const e = (parseInt(parts[1], 10) || (startYear + 1)) + (classNum - 1);
  return `${s}-${String(e).slice(-2)}`;
}

const GNM_CLASSES = [
  { value: 1, label: "1st Year GNM" },
  { value: 2, label: "2nd Year GNM" },
  { value: 3, label: "3rd Year GNM" }
];

const CATEGORY_LIST = [
  { value: "OPEN", label: "Open" },
  { value: "ST", label: "ST" },
  { value: "NT", label: "NT" },
  { value: "OBC", label: "OBC" },
  { value: "VJNT", label: "VJNT" },
  { value: "SBC", label: "SBC" },
  { value: "SC", label: "SC" }
];

const AddStudentModal = ({ token, onClose, onSave, show }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [address, setAddress] = useState('');
  const [rollNo, setRollNo] = useState('');
  const [batchId, setBatchId] = useState('');
  const [batches, setBatches] = useState([]);
  const [classYear, setClassYear] = useState(1);
  const [category, setCategory] = useState('OPEN');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (show) {
      setName('');
      setEmail('');
      setDob('');
      setAddress('');
      setRollNo('');
      setBatchId('');
      setClassYear(1);
      setCategory('OPEN');
      setError('');
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    fetch(`${API_URL}/api/batches`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setBatches(Array.isArray(data) ? data : []);
      })
      .catch(() => setBatches([]));
  }, [token]);

  const selectedBatch = batches.find(b => String(b.id) === String(batchId));
  const displaySessionYear = selectedBatch ? getSessionYear(selectedBatch, classYear) : "";

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!name || !email || !displaySessionYear || !rollNo || !batchId) {
      setError('Please fill all required fields and select a valid batch and class.');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/students`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name,
          email,
          dob,
          address,
          batch_id: batchId,
          course_year: classYear,
          session_year: displaySessionYear,
          roll_no: rollNo,
          category // <-- add category to backend
        })
      });
      if (res.ok) {
        onSave();
        onClose();
      } else {
        let data = {};
        try { data = await res.json(); } catch(e) {}
        setError(data.error || 'Error adding student.');
      }
    } catch (err) {
      setError('Server error');
    }
    setLoading(false);
  };

  if (!show) return null;

  return (
    <div className="student-modal">
      <form className="student-form" onSubmit={handleSubmit}>
        <h3>Add Student</h3>
        <div className="student-form-group">
          <label>Name</label>
          <input value={name} onChange={e => setName(e.target.value)} required />
        </div>
        <div className="student-form-group">
          <label>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
        </div>
        <div className="student-form-group">
          <label>Date of Birth</label>
          <input type="date" value={dob} onChange={e => setDob(e.target.value)} />
        </div>
        <div className="student-form-group">
          <label>Address</label>
          <input value={address} onChange={e => setAddress(e.target.value)} />
        </div>
        <div className="student-form-group">
          <label>Category</label>
          <select value={category} onChange={e => setCategory(e.target.value)} required>
            {CATEGORY_LIST.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="student-form-group">
          <label>Batch / Session Year</label>
          <select
            value={batchId}
            onChange={e => setBatchId(e.target.value)}
            required
          >
            <option value="">Select Batch / Session Year</option>
            {batches.map(batch => (
              <option key={batch.id} value={batch.id}>
                {batch.name} ({batch.start_year}–{batch.end_year}) Fee: ₹{parseFloat(batch.annual_fee).toLocaleString('en-IN', {minimumFractionDigits: 2})}
              </option>
            ))}
          </select>
        </div>
        <div className="student-form-group">
          <label>Class / Year</label>
          <select
            value={classYear}
            onChange={e => setClassYear(Number(e.target.value))}
            required
          >
            {GNM_CLASSES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="student-form-group">
          <label>Session Year (Auto)</label>
          <input value={displaySessionYear} readOnly style={{ background: "#e9ecef" }} />
        </div>
        <div className="student-form-group">
          <label>Roll No</label>
          <input value={rollNo} onChange={e => setRollNo(e.target.value)} required />
        </div>
        {error && <div className="student-form-error">{error}</div>}
        <div className="student-form-actions">
          <button type="submit" className="student-btn" disabled={loading}>Add</button>
          <button type="button" className="student-btn student-btn-cancel" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default AddStudentModal;